import PropTypes from "prop-types";
import React, { useState } from "react";
import { PulseLoader } from "react-spinners";
import {
  DndContext,
  closestCenter,
  useSensor,
  useSensors,
  PointerSensor,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";

import SortableDocument from "./SortableDocument";

const MAX_BYTE_SIZE = 10 * 1024 * 1024;

const ALLOWED_EXTENSIONS = [
  "pdf",
  "doc",
  "docx",
  "xls",
  "xlsx",
  "ppt",
  "pptx",
  "txt",
  "csv",
  "zip",
  "png",
  "jpg",
  "jpeg",
];

const csrfToken = () =>
  document.querySelector('[name="csrf-token"]').content;

const extensionOf = (filename) =>
  filename.split(".").pop().toLowerCase();

const CourseDocuments = (props) => {
  const { courseId } = props;

  const [documents, setDocuments] = useState(props.documents || []);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");

  const sensors = useSensors(useSensor(PointerSensor));

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (!over || active.id === over.id) return;

    const oldIndex = documents.findIndex((d) => d.id === active.id);
    const newIndex = documents.findIndex((d) => d.id === over.id);
    const reordered = arrayMove(documents, oldIndex, newIndex);

    setDocuments(reordered);
    persistOrder(reordered);
  };

  const persistOrder = async (reordered) => {
    const response = await fetch(`/courses/${courseId}/documents/order`, {
      method: "PUT",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        "X-CSRF-Token": csrfToken(),
      },
      body: JSON.stringify({ document_ids: reordered.map((d) => d.id) }),
    });

    if (response.ok) {
      const data = await response.json();
      setDocuments(data.documents);
    } else {
      setError("Nie udało się zapisać kolejności");
    }
  };

  const validate = (file) => {
    if (!ALLOWED_EXTENSIONS.includes(extensionOf(file.name))) {
      return "Niedozwolony format pliku";
    }
    if (file.size > MAX_BYTE_SIZE) {
      return "Plik przekracza limit 10 MB";
    }
    return "";
  };

  // Same upload path as lesson files: ask the app for a presigned S3 URL, PUT the file
  // straight to the bucket, then hand the key back. Keeping one flow means one set of
  // bucket CORS rules to maintain.
  const uploadToStorage = async (file) => {
    const presignUrl = `/courses/presigned_url?filename=${encodeURIComponent(
      file.name,
    )}&content_type=${encodeURIComponent(file.type)}`;

    const presign = await fetch(presignUrl, {
      method: "GET",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        "X-CSRF-Token": csrfToken(),
      },
    });

    if (!presign.ok) throw new Error("presign failed");

    const { url, key } = await presign.json();

    const upload = await fetch(url, {
      method: "PUT",
      headers: { "Content-Type": file.type },
      body: file,
    });

    if (!upload.ok) throw new Error("upload failed");

    return { key, content_type: file.type, byte_size: file.size };
  };

  const handleFile = async (file) => {
    if (!file) return;

    const validationError = validate(file);
    if (validationError) {
      setError(validationError);
      return;
    }

    setError("");
    setUploading(true);

    try {
      const uploaded = await uploadToStorage(file);

      const response = await fetch(`/courses/${courseId}/documents`, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken(),
        },
        body: JSON.stringify({
          document: {
            title: file.name.replace(/\.[^.]+$/, ""),
            file: uploaded,
          },
        }),
      });

      if (response.ok) {
        const data = await response.json();
        setDocuments([...documents, data.document]);
      } else {
        setError("Nie udało się zapisać materiału");
      }
    } catch {
      setError("Nie udało się wysłać pliku");
    } finally {
      setUploading(false);
    }
  };

  const handleRename = async (document, title) => {
    if (title === document.title) return;

    const response = await fetch(
      `/courses/${courseId}/documents/${document.id}`,
      {
        method: "PATCH",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken(),
        },
        body: JSON.stringify({ document: { title } }),
      },
    );

    if (response.ok) {
      const data = await response.json();
      setDocuments(
        documents.map((d) => (d.id === document.id ? data.document : d)),
      );
      setError("");
    } else {
      setError("Nazwa musi mieć co najmniej 2 znaki");
    }
  };

  const handleDelete = async (document) => {
    const response = await fetch(
      `/courses/${courseId}/documents/${document.id}`,
      {
        method: "DELETE",
        headers: {
          Accept: "application/json",
          "X-CSRF-Token": csrfToken(),
        },
      },
    );

    if (response.ok) {
      setDocuments(documents.filter((d) => d.id !== document.id));
    } else {
      setError("Nie udało się usunąć materiału");
    }
  };

  return (
    <div className="p-6 bg-white font-sans text-sm text-gray-800 w-3/4">
      <div className="border-b pb-2 mb-4 flex items-center justify-between">
        <span className="text-sm font-medium">Materiały</span>
        <span className="text-xs text-gray-500">
          Maksymalnie 10 MB na plik
        </span>
      </div>

      <label className="flex items-center justify-center gap-2 border border-dashed border-gray-300 px-4 py-6 cursor-pointer hover:bg-gray-50">
        <input
          type="file"
          className="hidden"
          onChange={(e) => {
            handleFile(e.target.files[0]);
            e.target.value = "";
          }}
        />
        {uploading ? (
          <PulseLoader size={8} />
        ) : (
          <span className="text-sm font-medium">Dodaj materiał</span>
        )}
      </label>

      {error && <div className="mt-3 text-sm text-red-600">{error}</div>}

      <div className="mt-6">
        {documents.length ? (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={documents.map((document) => document.id)}
              strategy={verticalListSortingStrategy}
            >
              {documents.map((document) => (
                <SortableDocument
                  key={document.id}
                  document={document}
                  onRename={handleRename}
                  onDelete={handleDelete}
                />
              ))}
            </SortableContext>
          </DndContext>
        ) : (
          <div className="text-sm text-gray-500">Brak materiałów</div>
        )}
      </div>
    </div>
  );
};

CourseDocuments.propTypes = {
  courseId: PropTypes.string,
  documents: PropTypes.array,
};

export default CourseDocuments;
